import { useEffect, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Custom hook to warn users before leaving a page during critical app flow.
 * Handles both browser-level navigation (tab close, refresh, external links)
 * and React Router navigation (in-app links).
 * 
 * @param {boolean} shouldWarn - Whether to show the warning
 * @param {string} message - Custom message for the confirm dialog
 * @param {{ allowPaths?: string[] }} options - Options object
 *   - allowPaths: Array of paths that are allowed without warning (e.g., next steps in flow)
 */
const useNavigationWarning = (
  shouldWarn = true,
  message = 'You have unsaved progress. Are you sure you want to leave?',
  options = {}
) => {
  const allowPaths = options.allowPaths || [];

  // Handle browser-level navigation (beforeunload)
  // This catches: tab close, refresh, typing new URL, external links
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!shouldWarn) return;
      
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
      return '';
    };

    if (shouldWarn) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldWarn]);

  // Handle React Router navigation (in-app navigation)
  // This catches: clicking internal links, programmatic navigate() calls
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        if (!shouldWarn) return false;

        // Don't warn if navigating to an allowed path (next step in flow)
        if (allowPaths.includes(nextLocation.pathname)) return false;

        // Warn for anything else (navbar links, back button, etc.)
        return currentLocation.pathname !== nextLocation.pathname;
      },
      [shouldWarn, allowPaths]
    )
  );

  // Show confirm dialog when blocker is triggered
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const confirmLeave = window.confirm(message);
      
      if (confirmLeave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, message]);

  return blocker;
};

export default useNavigationWarning;
